import { useState } from "react";
import { Avatar, Button, Flex, Table, TableProps, Typography, Tooltip, Form } from "antd";
import { Control, useFieldArray, Controller, useWatch, useFormState, UseFormTrigger, FieldValues } from "react-hook-form";
import { z } from "zod";
import { UserInfo } from "@entities/project";
import { RepresentationModal } from "@features/representation-modal";
import { BasketIcon, PlusAddIcon } from "@shared/assets/svg/icons";
import {
  AuthorizedRepresentative,
} from "../../model/add-record.schema";

type AuthorizedRepresentativeType = z.infer<typeof AuthorizedRepresentative>;

const getColumns = <T extends FieldValues>(
  index: number,
  control: Control<T>,
  // @ts-expect-error динамические ref по docType не типизированы в Record
  trigger: UseFormTrigger<T>,
  openModal: () => void,
  handleDeleteRow: (rowId: number) => void,
  isAddDisabled: boolean,
): TableProps<
  AuthorizedRepresentativeType["user"] & {
    rowIndex: number;
    isLast: boolean;
    id: string;
  }
>["columns"] => [
  {
    title: "ФИО",
    width: "20%",
    render: (value: AuthorizedRepresentativeType["user"]) => {
      return (
        <Flex align="center" gap="8px">
          <Avatar
            style={{
              backgroundColor: "#EAEEF8",
              color: "#888888",
              flex: "1 0 32px",
              width: "32px",
              height: "32px",
              fontSize: "14px",
              lineHeight: "20px",
            }}
          >
            {value.surname?.at(0)}
            {value.firstName?.at(0)}
          </Avatar>
          <Typography.Text>
            {value.surname} {value.firstName}
          </Typography.Text>
        </Flex>
      );
    },
  },
  {
    title: "Организация",
    width: "50%",
    render: (value: AuthorizedRepresentativeType["user"]) => {
      return value?.organization;
    },
  },
  {
    title: "Должность",
    width: "20%",
    render: (value: AuthorizedRepresentativeType["user"]) => {
      return value?.position;
    },
  },
  {
    align: "right",
    width: "74px",
    render: (value) => {
      return (
        <Controller
          name={`journalKeepersList.${index}.authorizedRepresentativesList.${value.rowIndex}` as any}
          control={control}
          render={() => (
            <Flex gap="10px" justify="flex-end">
              {value.isLast ? (
                <Tooltip title={isAddDisabled ? "Сначала выберите организацию" : undefined}>
                  <Button
                    onClick={openModal}
                    type="text"
                    icon={<PlusAddIcon />}
                    disabled={isAddDisabled}
                  />
                </Tooltip>
              ) : null}
              <Button
                onClick={() => handleDeleteRow(value.rowIndex)}
                type="text"
                icon={<BasketIcon />}
              />
            </Flex>
          )}
        />
      );
    },
  },
];

interface ParticipantsTableProps<T extends FieldValues> {
  index: number;
  control: Control<T>;
  trigger: UseFormTrigger<T>;
}

export function ParticipantsTable<T extends FieldValues>({ index, control, trigger }: ParticipantsTableProps<T>) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const company = useWatch({
    control,
    name: `journalKeepersList.${index}.company` as any,
  });

  const { errors } = useFormState({ control });

  const isCompanySelected = Boolean((company as any)?.projectParticipantId);

  const { fields, append, remove } = useFieldArray({
    control,
    name: `journalKeepersList.${index}.authorizedRepresentativesList` as any,
  });

  const validationError = (errors as any)?.journalKeepersList?.[index]?.authorizedRepresentativesList;
  const errorMessage = validationError?.message || (validationError && "Обязательное поле");

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalOpen = () => {
    if (!isCompanySelected) return;
    setIsModalOpen(true);
  };

  const handleAdd = (id?: string, userInfo?: UserInfo) => {
    if (id && userInfo) {
      const userCompany =
        userInfo.userCompanies?.find(
          ({ companyId }) => companyId === (company as any)?.companyId,
        ) ?? userInfo.userCompanies?.[0];

      append({
        user: {
          userId: id,
          userCompanyHistoryId: userCompany?.userCompaniesHistoryId,
          surname: userInfo.userSurname,
          firstName: userInfo.userFirstName,
          organization: userCompany?.shortName,
          position: userCompany?.position,
        },
      } as any);
      (trigger as any)(`journalKeepersList.${index}.authorizedRepresentativesList`);
      setIsModalOpen(false);
    }
  };

  const handleDelete = (id: number) => {
    remove(id);
    (trigger as any)(`journalKeepersList.${index}.authorizedRepresentativesList`);
  };

  const dataSource: Array<
    AuthorizedRepresentativeType["user"] & {
      rowIndex: number;
      isLast: boolean;
      id: string;
    }
  > = fields.map((value, fieldIndex, array) => {
    const v = value as any;
    return {
      id: value.id,
      firstName: v.user?.firstName,
      surname: v.user?.surname,
      userId: v.user?.userId,
      userCompanyHistoryId: v.user?.userCompanyHistoryId,
      rowIndex: fieldIndex,
      isLast: array.length - 1 === fieldIndex,
      organization: v.user?.organization,
      position: v.user?.position,
    };
  });

  return (
    <Controller
      control={control}
      name={`journalKeepersList.${index}.authorizedRepresentativesList` as any}
      render={({ fieldState }) => {
        const finalErrorMessage = errorMessage || fieldState.error?.message;

        return (
          <>
            <Form.Item
              validateStatus={finalErrorMessage ? "error" : ""}
              help={finalErrorMessage}
              style={{ margin: 0 }}
            >
              {fields.length > 0 ? (
                <Table
                  rowKey={(record) => record.id}
                  columns={getColumns<T>(index, control, trigger, handleModalOpen, handleDelete, !isCompanySelected)}
                  dataSource={dataSource}
                  pagination={false}
                />
              ) : (
                <>
                  <Tooltip title={!isCompanySelected ? "Сначала выберите организацию" : undefined}>
                    <Button
                      style={{ width: "fit-content" }}
                      type="primary"
                      onClick={handleModalOpen}
                      disabled={!isCompanySelected}
                    >
                      Добавить сотрудника
                    </Button>
                  </Tooltip>
                </>
              )}
            </Form.Item>
            <RepresentationModal
              isOpen={isModalOpen}
              handleClose={handleModalClose}
              handleAdd={handleAdd}
              projectParticipantId={company?.projectParticipantId || ""}
            />
          </>
        );
      }}
    />
  );
}
